import { pb } from '@/libs/pocketbase'
import { tryCatch } from '@/utils/try-catch'
import type { ChatMember, ChatPort, Message, Topic, TopicView } from '../domain/chat'

// Thin transport over the chat backend's custom routes. Request de-duplication
// is disabled (requestKey: null) so rapid sends/polls are never silently
// dropped, and the backend error message is surfaced verbatim for the use-cases
// to turn into toasts.

const query = (params: Record<string, string | number | null | undefined>) => {
	const search = new URLSearchParams()
	for (const [key, value] of Object.entries(params)) {
		if (value === null || value === undefined || value === '') continue
		search.set(key, String(value))
	}
	const value = search.toString()
	return value ? `?${value}` : ''
}

const get = async <T>(path: string): Promise<T> => {
	const result = await tryCatch(pb.send<T>(`/api/chat${path}`, { method: 'GET', requestKey: null }))
	if (!result.isSuccess) throw new Error(result.error.message)
	return result.data
}

const send = async <T>(method: 'POST' | 'DELETE', path: string, body?: unknown): Promise<T> => {
	const result = await tryCatch(
		pb.send<T>(`/api/chat${path}`, {
			method,
			requestKey: null,
			...(body === undefined
				? {}
				: { body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }),
		}),
	)
	if (!result.isSuccess) throw new Error(result.error.message)
	return result.data
}

const post = <T>(path: string, body?: unknown) => send<T>('POST', path, body)
const del = <T>(path: string, body?: unknown) => send<T>('DELETE', path, body)

type MemberRecord = {
	userId: string
	name: string
}

type UserRecord = {
	id: string
	name: string | null
	email: string | null
	avatar: string | null
}

// The member picker reads the shared users collection directly, mirroring the
// customer-lookup isolation pattern: chat owns its people lookup rather than
// importing the HR/members feature (which is permission-gated and carries a far
// richer model than a picker needs).
const listMembers: ChatPort['listMembers'] = async query => {
	const filter = query ? pb.filter('name ~ {:q} || email ~ {:q}', { q: query }) : ''
	const result = await tryCatch(
		pb.collection('users').getList<UserRecord>(1, 50, { filter, sort: 'name', requestKey: null }),
	)
	if (!result.isSuccess) throw new Error(result.error.message)
	return result.data.items.map<ChatMember>(record => ({
		id: record.id,
		name: record.name ?? record.email ?? record.id,
		avatarUrl: record.avatar
			? pb.files.getURL({ id: record.id, collectionName: 'users' } as never, record.avatar)
			: undefined,
	}))
}

const pbChat: ChatPort = {
	listTopics: () => get<TopicView[]>('/topics'),
	listMessages: (topicId, before, limit) =>
		get<Message[]>(`/topics/${topicId}/messages${query({ before, limit })}`),
	startP2P: peerId => post<Topic>('/topics/p2p', { peerId }),
	createGroup: (name, memberIds, publicMeta) =>
		post<Topic>('/topics/group', { name, memberIds, public: publicMeta }),
	getOrCreateEntityTopic: (kind, id) => get<Topic>(`/topics/by-entity${query({ kind, id })}`),
	publish: ({ topicId, content, head }) => post<Message>(`/topics/${topicId}/messages`, { content, head }),
	markRead: async (topicId, seqId) => {
		await post<void>(`/topics/${topicId}/read`, { seqId })
	},
	addMember: async (topicId, userId) => {
		await post<void>(`/topics/${topicId}/members`, { userId })
	},
	removeMember: async (topicId, userId) => {
		await del<void>(`/topics/${topicId}/members/${userId}`)
	},
	deleteMessage: async (messageId, scope) => {
		await del<void>(`/messages/${messageId}${query({ scope: scope === 'self' ? 'self' : null })}`)
	},
	listSubscriptions: async topicId => {
		const members = await get<MemberRecord[]>(`/topics/${topicId}/members`)
		return members.map<ChatMember>(m => ({ id: m.userId, name: m.name }))
	},
	listMembers,
}

export { pbChat }
